import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Dumbbell, User, CalendarDays, ScanLine, Mic, TrendingUp, ShoppingBag } from 'lucide-react';
import { hapticSelection } from '../lib/telegram';
import { useTranslation } from 'react-i18next';

// Primary nav — matches App Store screenshots (Home / Workouts / Coach / Progress / Profile).
// Calendar (History) and Oracle (Nutri Vision) moved to MORE menu (long-press or settings).
// Shop also in MORE (rarely tapped before week 4 of usage).
const MAIN_ITEMS = [
  { path: '/',         icon: Home,       labelKey: 'nav.home' },
  { path: '/workout',  icon: Dumbbell,   labelKey: 'nav.workout' },
  { path: '/coach',    icon: Mic,        labelKey: 'nav.coach' },
  { path: '/progress', icon: TrendingUp, labelKey: 'nav.progress' },
  { path: '/profile',  icon: User,       labelKey: 'nav.profile' },
];

// Secondary destinations — accessible via Profile screen or direct deep-link.
// Kept exported so future Settings → Quick Actions surface can re-use.
export const MORE_ITEMS = [
  { path: '/calendar', icon: CalendarDays, labelKey: 'nav.history', color: '#A78BFA' },
  { path: '/oracle',   icon: ScanLine,     labelKey: 'nav.nutri',   color: '#00E5CC' },
  { path: '/shop',     icon: ShoppingBag,  labelKey: 'nav.shop',    color: '#C9A84C' },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleNav = (path) => {
    if (location.pathname === path) return;
    hapticSelection();
    navigate(path);
  };

  return (
    <>
      {/* Main bottom nav.
          NOT: background `<nav>` üzerinde olmalı (içerideki div'de değil),
          aksi halde home indicator alanı (env(safe-area-inset-bottom))
          transparan kalıyor, iPhone Pro/Plus'ta belirgin boşluk görünüyor. */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          background: 'rgba(18, 18, 26, 0.92)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
        aria-label={t('nav.primaryAria', 'Primary navigation')}
      >
        <div className="rounded-t-[2rem] border-t border-[rgba(255,255,255,0.08)]">
          <div className="flex items-center justify-around px-2 py-2 max-w-lg mx-auto">
            {MAIN_ITEMS.map(({ path, icon: Icon, labelKey }) => {
              const isActive = location.pathname === path;
              return (
                <motion.button
                  key={path}
                  onClick={() => handleNav(path)}
                  aria-label={t(labelKey)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`
                    relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl min-h-[44px] justify-center
                    transition-colors duration-200
                    ${isActive ? 'text-[#00E5CC]' : 'text-[#64748B] hover:text-[#94A3B8]'}
                  `}
                  whileTap={{ scale: 0.9 }}
                >
                  <Icon
                    className={`w-5 h-5 transition-all duration-200 ${
                      isActive ? 'drop-shadow-[0_0_12px_rgba(0,229,204,0.8)]' : ''
                    }`}
                    strokeWidth={isActive ? 2.5 : 1.5}
                  />
                  <span className={`font-sans text-[10px] tracking-[1px] uppercase ${isActive ? 'text-[#00E5CC]' : ''}`}>
                    {t(labelKey)}
                  </span>
                  {isActive && (
                    <motion.div
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full"
                      style={{
                        background: '#00E5CC',
                        boxShadow: '0 0 8px rgba(0,229,204,0.6), 0 0 16px rgba(0,229,204,0.3)',
                      }}
                      layoutId="nav-indicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </motion.button>
              );
            })}

          </div>
        </div>
      </nav>
    </>
  );
}
